/**
 * SACP — canonical USAGE-STATUS rendering contract (pure, testable).
 *
 * The backend usage resolver returns each metric with an explicit status. The UI must render by
 * status and NEVER coerce an uncalculated/unavailable metric to 0. Numeric limits (seats/engagements/
 * AI/storage/slots) are NOT Boolean features and must not show "Locked/Upgrade" when the tenant holds
 * a positive or Unlimited effective allowance.
 *
 * Web consumes this now; the Android/iOS Business apps must mirror the same status handling
 * (value ?: 0 / ?? 0 on an uncalculated metric is a defect).
 */
export const UNLIMITED = -1;

/** Render a usage metric line from a canonical metric object {used, limit, status}. */
export function renderUsage(metric, { unit } = {}) {
  if (!metric) return 'Usage temporarily unavailable';
  const { used, limit, status } = metric;
  const lim = (limit === UNLIMITED || limit === 'unlimited' || limit === 'Unlimited') ? 'Unlimited' : limit;
  switch (status) {
    case 'not_calculated':
      // Honest no-contract state: the metric has no runtime meter/accounting writer yet, so there is
      // genuinely no value to show. NOT a transient failure and NOT a false zero. Copy is the established
      // shared product string (matches the iOS Business usageStatus port + the contract test) — kept in
      // parity across planes; do not drift it in one plane only.
      return 'Usage not yet calculated';
    case 'unavailable':
      // Transient: the authoritative source threw while resolving. Real error, not a no-contract state.
      return 'Usage temporarily unavailable';
    case 'reconciliation_required':
      return 'Usage reconciliation required';
    case 'real':
    case 'authenticated_zero': {
      const u = Number(used || 0).toLocaleString();
      if (lim === 'Unlimited') return `${u} used of Unlimited${unit ? ' ' + unit : ''}`;
      if (lim == null) return `${u} used${unit ? ' ' + unit : ''}`;
      return `${u} of ${Number(lim).toLocaleString()}${unit ? ' ' + unit : ''} used`;
    }
    default:
      return 'Usage temporarily unavailable';
  }
}

/** Allowance line, e.g. "Allowance: 2,000 per billing period". Never a fabricated ceiling. */
export function allowanceLine(metric, { unit, perPeriod } = {}) {
  if (!metric) return null;
  const lim = metric.limit;
  const v = (lim === UNLIMITED || lim === 'Unlimited') ? 'Unlimited'
    : (lim == null ? null : Number(lim).toLocaleString());
  if (v == null) return null;
  return `Allowance: ${v}${unit ? ' ' + unit : ''}${perPeriod ? ' per billing period' : ''}`;
}

/** True only when a metric has a real numeric progress bar (i.e. an authoritative used vs finite limit). */
export function hasProgressBar(metric) {
  if (!metric || (metric.status !== 'real' && metric.status !== 'authenticated_zero')) return false;
  const lim = metric.limit;
  return lim != null && lim !== UNLIMITED && lim !== 'Unlimited';
}

/** Progress fraction 0..1, or null when a bar must not render (uncalculated/unavailable/unlimited). */
export function progressFraction(metric) {
  if (!hasProgressBar(metric)) return null;
  const used = Number(metric.used || 0);
  const lim = Number(metric.limit);
  if (!lim) return 0;
  return Math.max(0, Math.min(1, used / lim));
}

// ── numeric-limit vs Boolean feature classification ──────────────────────────
const NUMERIC_LIMIT_KEYS = new Set([
  'max_staff', 'monthly_engagements', 'monthly_ai_resolutions', 'storage_gb', 'active_integrations',
]);

/**
 * A PLATFORM-MANAGED capability (tenant_scope="all_registered_businesses", e.g. Servisfy Status & Updates) is
 * delivered to every registered Business and is NOT tenant-entitlement-gated — it must not render as
 * "Not entitled / Grant / Deny". Canonical metadata only (never a hardcoded key).
 */
export function isPlatformManaged(rec) {
  return !!rec && rec.tenant_scope === 'all_registered_businesses';
}

/**
 * Classify a feature/entitlement record so numeric limits are never treated as Boolean features.
 * PRIMARY authority is backend-declared metadata (metric_type / value_type / feature_type / entitlement_type).
 * The hardcoded numeric-limit key set is only a BACKWARD-COMPAT fallback for old rows that predate the metadata
 * pass-through — it is NEVER the primary authority and NEVER infers from the display name.
 */
export function entitlementValueType(rec) {
  if (!rec) return 'unknown';
  const declared = rec.metric_type || rec.value_type || rec.feature_type || rec.entitlement_type;
  if (declared === 'numeric_limit' || declared === 'quantity' || declared === 'metered') return 'numeric_limit';
  if (declared === 'boolean') return 'boolean';
  if (declared === 'configuration') return 'configurable';
  if (declared === 'pack' || declared === 'add_on') return 'pack';
  // canonical category is also authoritative: any commercial LIMIT is a numeric limit (generic, no key list).
  if ((rec.catalog_category || rec.category) === 'limit') return 'numeric_limit';
  const key = rec.feature_key || rec.key || rec.limit_key;
  if (key && NUMERIC_LIMIT_KEYS.has(key)) return 'numeric_limit';   // legacy fallback only
  return 'boolean';
}

/** A numeric limit with a positive/Unlimited effective allowance must never render as "Locked". */
export function isEntitledNumericLimit(rec, allowance) {
  if (entitlementValueType(rec) !== 'numeric_limit') return false;
  return allowance === UNLIMITED || allowance === 'Unlimited' || (typeof allowance === 'number' && allowance > 0);
}
